import React from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, ProjectionVariant } from 'src/api/types';
import { DataTitle, DataValue, NoData, Spacer, VariantValues } from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import ExpandSection from 'src/components/ExpandSection';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { formatDate, numeralFormat } from 'src/utils/misc';
import styled from 'styled-components';
import { genFrequencyLabel } from '../util';
import { ScenarioQuery } from 'src/graphql/_generated';
import { LABELS } from 'src/components/labels';
import { isNil } from 'lodash';

const Div = styled.div`
  .note-text {
    word-break: break-word;
  }

  .content {
    display: flex;
    justify-content: space-between;
  }

  .notes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 30px;
    &__values {
      display: flex;
      flex-direction: column;
      grid-gap: 12px;
      align-items: flex-start;
    }
  }
  .consequences {
    display: flex;
    grid-gap: 30px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .risk-statement {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-gap: 10px;
    }
    .notes {
      grid-template-columns: 1fr;
      grid-gap: 10px;
    }
    .consequences {
      flex-direction: column;
      grid-gap: 15px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .risk-statement {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

interface RiskStatementSectionProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
  showAllProjected: boolean;
}

export const RiskStatementSection: React.FC<RiskStatementSectionProps> = ({ scenario, showAllProjected }) => {
  return (
    <ExpandSection title="RISK STATEMENT" sectionId="scenario_rs">
      <Div>
        <div className="content risk-statement">
          <div>
            <DataTitle>SOURCES</DataTitle>
            {!isNil(scenario.source) ? (
              <DataValue data-cy="scenario-source">{getRiskSourcesName(scenario.source)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>EVENTS</DataTitle>
            {!isNil(scenario.event) ? (
              <DataValue data-cy="scenario-event">{getRiskEventsName(scenario.event)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>CONSEQUENCES</DataTitle>
            {!isNil(scenario.consequence) ? (
              <DataValue data-cy="scenario-consequence">{getRiskConsequencesName(scenario.consequence)}</DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <DataTitle>IDENTIFIED DATE</DataTitle>
            {scenario.riskIdentifiedDate ? (
              <DataValue data-cy="scenario-risk_identified_date">
                <TLLink date={scenario.riskIdentifiedDate}>{formatDate(scenario.riskIdentifiedDate)}</TLLink>
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
        </div>
        <Spacer $px={30} />
        <DataTitle>RISK NARRATIVE</DataTitle>
        <div />
        {scenario.riskNarrative ? <ExpandCollapse text={scenario.riskNarrative} /> : <NoData />}
        <Spacer $px={30} />
        <div className="notes">
          <div>
            <DataTitle>FREQUENCY</DataTitle>

            {scenario.liveRisk.lowerThanMinimum?.includes('frequency') ? (
              <DataValue data-cy="scenario-frequency">Frequency lower than 1 time every 100 years</DataValue>
            ) : (
              <>
                <Spacer $px={4} />
                {showAllProjected ? (
                  <div className="notes__values">
                    {genFrequencyLabel({
                      scenario: {
                        frequency_times: scenario.baseRisk?.frequencyTimes,
                        frequency_years: scenario.baseRisk?.frequencyYears,
                      },
                      tooltipText: 'Frequency without control modifiers applied',
                      variant: ProjectionVariant.base,
                    })}
                    {genFrequencyLabel({
                      scenario: {
                        frequency_times: scenario.liveRisk?.frequencyTimes,
                        frequency_years: scenario.liveRisk?.frequencyYears,
                      },
                      tooltipText: 'Frequency with control modifiers from live controls applied',
                      variant: ProjectionVariant.live,
                    })}
                    {genFrequencyLabel({
                      scenario: {
                        frequency_times: scenario.projectedRisk?.frequencyTimes,
                        frequency_years: scenario.projectedRisk?.frequencyYears,
                      },
                      tooltipText: 'Frequency with control modifiers from all controls applied',
                      variant: ProjectionVariant.complete,
                    })}
                  </div>
                ) : (
                  genFrequencyLabel({
                    scenario: {
                      frequency_times: scenario.liveRisk?.frequencyTimes,
                      frequency_years: scenario.liveRisk?.frequencyYears,
                    },
                  })
                )}
              </>
            )}
            <Spacer $px={30} />
            <DataTitle>NOTE</DataTitle>
            {scenario.frequencyNote ? (
              <DataValue className="note-text" data-cy="scenario-frequency-note">
                {scenario.frequencyNote}
              </DataValue>
            ) : (
              <NoData />
            )}
          </div>
          <div>
            <div className="consequences">
              <div>
                <DataTitle>CONSEQUENCE LOWER</DataTitle>
                {scenario.liveRisk.lowerThanMinimum?.includes('lower') ? (
                  <DataValue data-cy="scenario-lower">{'<1'}</DataValue>
                ) : (
                  <>
                    {showAllProjected ? (
                      <>
                        <Spacer $px={4} />
                        <VariantValues
                          value={{
                            base: scenario.baseRisk?.lower,
                            live: scenario.liveRisk?.lower,
                            cpmplete: scenario.projectedRisk?.lower,
                          }}
                          tooltipText={{
                            base: LABELS.modifierBaseConsequence,
                            live: LABELS.modifierConsequenceImpact,
                            cpmplete: LABELS.modifierCumulativeImpact,
                          }}
                        />
                      </>
                    ) : (
                      <>
                        {scenario.liveRisk.lower === null ? (
                          <div style={{ marginLeft: '5px' }}>
                            <NoData techIncomplete />
                          </div>
                        ) : (
                          <DataValue data-cy="scenario-lower">{numeralFormat(scenario.liveRisk.lower)}</DataValue>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <div>
                <DataTitle>CONSEQUENCE UPPER</DataTitle>
                {scenario.liveRisk.lowerThanMinimum?.includes('upper') ? (
                  <DataValue data-cy="scenario-upper">{'<1'}</DataValue>
                ) : (
                  <>
                    {scenario.liveRisk.upper === null ? (
                      <div style={{ marginLeft: '5px' }}>
                        <NoData techIncomplete />
                      </div>
                    ) : (
                      <>
                        {showAllProjected ? (
                          <>
                            <Spacer $px={4} />
                            <VariantValues
                              value={{
                                base: scenario.baseRisk?.upper,
                                live: scenario.liveRisk?.upper,
                                cpmplete: scenario.projectedRisk?.upper,
                              }}
                              tooltipText={{
                                base: 'Consequence without control modifiers applied',
                                live: 'Consequence with control modifiers from live controls applied',
                                cpmplete: 'Consequence with control modifiers from all controls applied',
                              }}
                            />
                          </>
                        ) : (
                          <>
                            {scenario.liveRisk.upper === null ? (
                              <div style={{ marginLeft: '5px' }}>
                                <NoData techIncomplete />
                              </div>
                            ) : (
                              <DataValue data-cy="scenario-upper">{numeralFormat(scenario.liveRisk.upper)}</DataValue>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <Spacer $px={30} />
            <div>
              <DataTitle>NOTE</DataTitle>
              {scenario.consequenceNote ? (
                <DataValue className="note-text" data-cy="scenario-sonsequence-note">
                  {scenario.consequenceNote}
                </DataValue>
              ) : (
                <NoData />
              )}
            </div>
          </div>
        </div>
      </Div>
    </ExpandSection>
  );
};
